import moment from 'moment'
import * as Ru from '../../common/ramda-utils'
import { CommercialTerms, Offer } from '../../../types/types'
import { CommercialTermKey } from 'nemo-pricing'
import { CommercialTermRow } from 'app/types/tables'
import { getIsBillingModeApplicable, isMultiSelectCommercialTerm } from '../utils/commercialterms/CommercialTermsUtils'

const minDate = moment().subtract(1, 'year').startOf('year').subtract(1, 'day')
const maxDate = moment([2201, 1, 1])

export const maxLength = 30

export function parseDate(date: string): moment.Moment {
  return moment(date, 'YYYY-MM-DD')
}

function isValidDate(date: moment.Moment) {
  return moment.isMoment(date) && date.isValid() && date.isAfter(minDate) && date.isBefore(maxDate)
}

export function isAfter(date: moment.Moment, maybeStartDate: moment.Moment) {
  return isValidDate(maybeStartDate) ? date.isAfter(maybeStartDate) : true
}

function isBefore(date: moment.Moment, maybeEndDate: moment.Moment) {
  return isValidDate(maybeEndDate) ? date.isBefore(maybeEndDate) : true
}

function validateDate(currentOfferCommercialTerms: CommercialTerms, key: CommercialTermKey, value: string) {
  // Guard against undefined commercialTerms
  if (!currentOfferCommercialTerms) {
    return false
  }

  // Guard against undefined or null value being validated (generic check)
  if (!value) {
    return false
  }

  const { VBEGDAT, VENDDAT, VABNDAT } = currentOfferCommercialTerms

  // Parse reference dates only if they exist
  const startDate = VBEGDAT ? parseDate(VBEGDAT as string) : null
  const endDate = VENDDAT ? parseDate(VENDDAT as string) : null
  const acceptanceDate = VABNDAT ? parseDate(VABNDAT as string) : null

  switch (key) {
    case 'VENDDAT':
      return endDate ? isValidDate(endDate) && (startDate ? isAfter(endDate, startDate) : true) : false
    case 'VABNDAT':
      return acceptanceDate ? isValidDate(acceptanceDate) && (startDate ? isAfter(acceptanceDate, startDate) : true) : false
    case 'KCSM_BASE_MONTH_ESCALATION':
      const escalationDate = parseDate(value)
      return isValidDate(escalationDate) &&
             (startDate ? isAfter(escalationDate, startDate) : true) &&
             (endDate ? isBefore(escalationDate, endDate) : true)
    default:
      return isValidDate(parseDate(value))
  }
}

export const validateMultiSelectCommercialTerm = (
  commercialTerm: CommercialTermRow,
  currentOfferCommercialTerms: CommercialTerms,
  validateOptional: boolean
) => {
  // Guard against undefined commercialTerms
  if (!currentOfferCommercialTerms) {
    return false
  }

  const key = commercialTerm.key
  const optional = commercialTerm.optional
  const value = currentOfferCommercialTerms[key]

  if (validateOptional && Ru.isNilOrEmpty(value)) {
    return optional
  }
  return !(!commercialTerm.is_multiselect && Array.isArray(value) && value.length > 1) // ? false : true
}

export const validateCommercialTerm = (
  currentOfferCommercialTerms: CommercialTerms,
  commercialTerm: CommercialTermRow,
  isContractStartDateAtEquipmentLevel: boolean,
  isBillingModeApplied: boolean,
  isBillingPlanAtEquipmentLevel?: boolean,
  isOrderReasonAtEquipmentLevel?: boolean
): boolean => {
  // Guard against undefined commercialTerms
  if (!currentOfferCommercialTerms) {
    return false
  }

  const key = commercialTerm.key
  const optional = commercialTerm.optional
  const value = currentOfferCommercialTerms[key]

  switch (true) {
    case ['VBEGDAT', 'KCSM_BASE_MONTH_ESCALATION', 'VENDDAT', 'PROPORTIONAL_PRICE', 'VABNDAT', 'KONDA'].includes(key) &&
      isContractStartDateAtEquipmentLevel:
      return true

    case key === 'KCSM_BILLING_MODE' && !isBillingModeApplied:
      return true

    case key === 'KCSM_BILLING_PLAN' && isBillingPlanAtEquipmentLevel:
      return true

    case key === 'AUGRU' && isOrderReasonAtEquipmentLevel:
      return true

    // Empty string counts as "no value" too, not just null/undefined - optional fields must not fail here
    case Ru.isNilOrEmpty(value):
      return optional
  }

  switch (commercialTerm.type) {
    case 'date':
      return validateDate(currentOfferCommercialTerms, key, value as string)
    case 'flexible-date':
      return (value && value === 'flexible') || validateDate(currentOfferCommercialTerms, key, value as string)
    case 'text':
      return value ? (value.length as number) <= maxLength : false
    default:
      return isMultiSelectCommercialTerm(key)
        ? validateMultiSelectCommercialTerm(commercialTerm, currentOfferCommercialTerms, true)
        : !Ru.isNilOrEmpty(value)
  }
}

export const areCommercialTermsValid =
  (commercialTermRow: CommercialTermRow[], _isEscalationFlow: boolean = false) =>
  (offer: Offer) => {
    // We are filtering commercial terms for escalation flow.
    // const filteredCommercialTerms = isEscalationFlow
    //   ? getEscalationFlowCommercialTerms(commercialTermRow)
    //   : commercialTermRow
    const filteredCommercialTerms = commercialTermRow
    //   ? getEscalationFlowCommercialTerms(commercialTermRow)
    //   : commercialTermRow
    // const answers = offer.commercialTerms
    const {
      isContractStartDateAtEquipmentLevel,
      isBillingPlanAtEquipmentLevel,
      isOrderReasonAtEquipmentLevel,
      equipmentGroups,
      commercialTerms
    } = offer
    const isBillingModeApplied = getIsBillingModeApplicable(equipmentGroups)
    const results = filteredCommercialTerms.map(term =>
      validateCommercialTerm(
        commercialTerms,
        term,
        isContractStartDateAtEquipmentLevel,
        isBillingModeApplied,
        isBillingPlanAtEquipmentLevel,
        isOrderReasonAtEquipmentLevel
      )
    )
    return results.every(r => r)
  }
